import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  ref,
  watch,
  withDefaults,
} from "vue";

/**
 * Types
 */

interface Option {
  text: string;
  icon: string;
  type: "history" | "generic";
}

type OptionCollection = Array<Option>;

type OptionCollections = Array<OptionCollection>;

interface DropdownOption extends Option {
  prependText: string;
  highlightedText: string;
  appendText: string;
}

type DropdownOptions = Array<DropdownOption>;

type Props = {
  placeholder: string;
  noResult: string;
  optionCollections: OptionCollections;
  value: string;
  isDisabled?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'VideoSearchbar',
  props: {
    placeholder: null,
    noResult: null,
    optionCollections: null,
    value: null,
    isDisabled: { type: Boolean, default: false }
  },
  emits: ["input", "enter-key-press", "option-click"],
  setup(__props: any, { emit }: { emit: ({
  (e: "input", value: string): void;
  (e: "enter-key-press", value: string): void;
  (e: "option-click", value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { placeholder: string, noResult: string, optionCollections: OptionCollections, value: string, isDisabled: boolean };



/**
 * Constants
 */

const MAX_OPTION_COUNT = {
  history: 4,
  generic: 1,
};

/**
 * Utils
 */

const getDropdownOption = (option: DropdownOption) => option;

const highlightMatchingOptions = ({
  options,
  currentSearchQuery,
}: {
  options: OptionCollection;
  currentSearchQuery: string;
}) =>
  options.flatMap((option) => {
    const currentOption = option.text.toLowerCase();

    const substringMatchStartIndex = currentOption.indexOf(currentSearchQuery);
    const substringMatchEndIndex =
      substringMatchStartIndex + currentSearchQuery.length;

    // Omit/remove the option
    if (substringMatchStartIndex === -1) {
      return [];
    }

    // Add dropdown properties to the option
    return [
      getDropdownOption({
        ...option,
        prependText: currentOption.substring(0, substringMatchStartIndex),
        highlightedText: currentOption.substring(
          substringMatchStartIndex,
          substringMatchEndIndex
        ),
        appendText: currentOption.substring(substringMatchEndIndex),
      }),
    ];
  });

/**
 * Component
 */



const searchQuery = computed({
  get: () => props.value,
  set: (newValue) => {
    if (newValue !== props.value) {
      emit("input", newValue);
    }
  },
});

const lowerCaseSearchQuery = computed(() => searchQuery.value.toLowerCase());

// History options sorted from option collections
const historyOptions = computed(() => {
  const historyCollection = props.optionCollections.find(
    (collection) => collection[0].type === "history"
  );

  return historyCollection || [];
});

const initialHistoryDropdownOptions = computed<DropdownOptions>(() => {
  // Limit to 5 items
  const matches: OptionCollection = historyOptions.value.filter(
    (_, index) => index <= MAX_OPTION_COUNT.history - 1
  );

  // Add dropdown properties to the options
  return matches.map((match) =>
    getDropdownOption({
      ...match,
      prependText: match.text,
      highlightedText: "",
      appendText: "",
    })
  );
});

const historyDropdownOptions = computed<DropdownOptions>(() => {
  const currentSearchQuery = lowerCaseSearchQuery.value;

  // If any search query
  if (currentSearchQuery && currentSearchQuery.trim()) {
    const matches: OptionCollection = historyOptions.value
      .filter(
        (option) => option.text.toLowerCase().indexOf(currentSearchQuery) !== -1
      )
      .filter((_, index) => index <= MAX_OPTION_COUNT.history - 1);

    return highlightMatchingOptions({
      options: matches,
      currentSearchQuery: lowerCaseSearchQuery.value,
    });
  }

  // If no search query => return initial history dropdown options
  return initialHistoryDropdownOptions.value;
});

const genericOptionCollections = computed(() =>
  props.optionCollections.filter(
    (collection) => collection[0].type === "generic"
  )
);

const initialGenericDropdownOptions = computed<DropdownOptions>(() => {
  const matches: OptionCollection = [];

  // Limit to single suggestion from a collection
  for (let i = 0; i < genericOptionCollections.value.length; i += 1) {
    for (let j = 0; j < genericOptionCollections.value[i].length; j += 1) {
      const shouldSkipOtherOptions = j === MAX_OPTION_COUNT.generic - 1;
      matches.push(genericOptionCollections.value[i][j]);

      if (shouldSkipOtherOptions) {
        break;
      }
    }
  }

  // Add dropdown properties to the options
  return matches.map((match) =>
    getDropdownOption({
      ...match,
      prependText: match.text,
      highlightedText: "",
      appendText: "",
    })
  );
});

const genericDropdownOptions = computed<DropdownOptions>(() => {
  const currentSearchQuery = lowerCaseSearchQuery.value;

  // If any search query
  if (currentSearchQuery && currentSearchQuery.trim()) {
    // Collection of first match from each set
    const matches: OptionCollection = [];

    genericOptionCollections.value.forEach((collection) => {
      // Find the first match
      const match = collection.find(
        (option) => option.text.toLowerCase().indexOf(currentSearchQuery) !== -1
      );

      if (match) {
        matches.push(match);
      }
    });

    return highlightMatchingOptions({
      options: matches,
      currentSearchQuery: lowerCaseSearchQuery.value,
    });
  }

  // If no search query => return initial generic dropdown options
  return initialGenericDropdownOptions.value;
});

const allDropdownOptions = computed<DropdownOptions>(() => [
  ...historyDropdownOptions.value,
  ...genericDropdownOptions.value,
]);

const focusedIndex = ref(-1);

const inputRef = ref<HTMLInputElement | null>(null);

const resetFocusedIndex = () => {
  if (inputRef.value) {
    inputRef.value.blur();
  }
  focusedIndex.value = -1;
};

const isDropdownVisible = ref<boolean>(false);

const handleSearchbarClick = () => {
  isDropdownVisible.value = true;
  focusedIndex.value = -1;
};

const hideDropdown = () => {
  isDropdownVisible.value = false;
  resetFocusedIndex();
};

const searchbarRef = ref<HTMLDivElement | null>(null);

const focusPreviousOption = () => {
  if (focusedIndex.value > 0) {
    focusedIndex.value -= 1;
  }
};

const focusNextOption = () => {
  if (focusedIndex.value < allDropdownOptions.value.length - 1) {
    focusedIndex.value += 1;
  }
};

const startArrowKeys = () => {
  if (isDropdownVisible.value) {
    focusNextOption();
  }
};

const focusOption = () => {
  if (searchbarRef.value) {
    searchbarRef.value?.children?.[1].children?.[focusedIndex.value]?.focus();
  }
};

watch(focusedIndex, () => {
  focusOption();
});

const handleDropdownOpionClick = (dropdownOption: DropdownOption) => {
  searchQuery.value = dropdownOption.text;

  emit("option-click", dropdownOption.text);
  hideDropdown();
};

const handleEnterKeyPressForNewQuery = (term: string) => {
  emit("enter-key-press", term);
  hideDropdown();
};

const handleEnterKeyPress = (event: KeyboardEvent) => {
  const currentOption = allDropdownOptions.value[focusedIndex.value];

  // For dropdown options - which already exists
  if (currentOption) {
    handleDropdownOpionClick(currentOption);
    return;
  }

  // For a new term - which is not there in the suggestions
  const newTerm = event.target?.value;
  if (newTerm) {
    handleEnterKeyPressForNewQuery(newTerm);
  }
};

const handleClear = () => {
  searchQuery.value = "";

  if (inputRef.value) {
    inputRef.value.focus();
    isDropdownVisible.value = true;
  }
};

const shouldShowSearchResults = computed(
  () => isDropdownVisible.value && allDropdownOptions.value.length
);

const shouldAllowInteraction = computed(() => !props.isDisabled);

return { __sfc: true,props, MAX_OPTION_COUNT, getDropdownOption, highlightMatchingOptions, emit, searchQuery, lowerCaseSearchQuery, historyOptions, initialHistoryDropdownOptions, historyDropdownOptions, genericOptionCollections, initialGenericDropdownOptions, genericDropdownOptions, allDropdownOptions, focusedIndex, inputRef, resetFocusedIndex, isDropdownVisible, handleSearchbarClick, hideDropdown, searchbarRef, focusPreviousOption, focusNextOption, startArrowKeys, focusOption, handleDropdownOpionClick, handleEnterKeyPressForNewQuery, handleEnterKeyPress, handleClear, shouldShowSearchResults, shouldAllowInteraction }
}

})